<script setup lang="ts">
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import CardLabels from './CardLabels.vue';
import HotelPoints from './HotelPoints.vue';
import StarRating from '@components/util/StarRating.vue';
import HotelPackage from '@itinerary/hotel/HotelPackage';
import { type PhotoSizeCollection } from '@clients/HotelApi';
import type { HotelUrlParams } from '@utils/hotel';
import { toAbsoluteSrc } from '@store/content';

const {
    hotelItem,
    disableTitleLink,
    loading,
    pricesLoading,
    mobileView,
    mobileObserving,
    isStatic,
    floatingEnabled,
    singleImg,
} = defineProps<IProps>();

const emit = defineEmits<{
    reserve: [];
    intersect: [];
    mouseenter: [event: MouseEvent];
    mouseleave: [event: MouseEvent];
}>();

const ImageCarousel = defineAsyncComponent(
    () => import('../ImageCarousel.vue')
);

// IMPORTANT: THE FOLLOWING FUNCTION IS PURE.
// TO KEEP THIS COMPONENT PURE, DON'T USE ANY IMPURE FUNCTIONS FROM STORE/MODULE.
const hotelForm = useHotelForm();
interface IProps {
    hotelItem?: HotelPackage;
    mobileView?: boolean;
    loading?: boolean;
    pricesLoading?: boolean;
    mobileObserving?: boolean;
    disableTitleLink?: boolean;
    isStatic?: boolean;
    preloadFirstImage?: boolean;
    floatingEnabled?: boolean;
    singleImg?: boolean;
}

const { isWebApp } = useDevice();
const router = useRouter();

const hotelCardContainer = ref<HTMLDivElement>();
const showPriceLoading = computed(() => pricesLoading && !payable.value);
const { isMobile } = useSize();
const loadingHotelPackage = new HotelPackage({
    hotel: {
        name: '....................................',
        englishName: '....................',
        englishCityName: '..........',
        hotelTypeId: 1,
        hotelTypeTitle: '..........',
        hotelTypeStars: 5,
        photo: '',
        isMrBilitOffer: false,
        shortAddress: '.......................',
        totalPrice: 0,
        minRoomPriceCapacity: 2,
        payable: 0,
        amenities: [],
        rating: 4,
        saleCount: 0,
        bannerPhotos: [],
        hasFreeTransfer: false,
        sortWeight: 1.652,
    },
});
const observer = ref<IntersectionObserver>();
const buttonLink = computed(() => {
    if (loading) {
        return undefined;
    } else {
        return true;
    }
});
const item = computed(() => {
    if (loading || !hotelItem) {
        return loadingHotelPackage;
    } else {
        return hotelItem;
    }
});
const discountedPrice = computed(() =>
    (payable.value || 0) < (totalPrice.value || 0)
        ? totalPrice.value || 0
        : null
);
const totalPrice = computed(() => item.value.totalPrice || null);
const payable = computed(() => item.value.payable || 0);
const percent = computed(() =>
    Math.ceil(100 - ((payable.value || 0) / (totalPrice.value || 0)) * 100)
);
const photos = computed<PhotoSizeCollection[]>(() => {
    if (item.value.photos?.length) {
        return item.value.photos;
    } else {
        const url = toAbsoluteSrc(
            '/uploads/hotel_placeholder_v4_af8e033460.png'
        );
        return [
            {
                large: url,
                original: url,
                small: url,
                alt: 'hotel placeholder',
            },
        ];
    }
});
const hasNoPhotos = computed(() => !loading && !item.value.photos.length);
const selectRoom = () => {
    if (loading) {
        return;
    }
    reserveHotel();
};
const cardClick = () => {
    if (isMobile.value || mobileView || isStatic) {
        selectRoom();
    }
};
const startObserving = () => {
    if (typeof IntersectionObserver === 'undefined') {
        return;
    }
    observer.value = new IntersectionObserver(
        entries => {
            if (entries[0].isIntersecting) {
                emit('intersect');
            }
        },
        {
            threshold: 0.7,
            root: null,
            rootMargin: '0px 0px 0px 0px',
        }
    );
    if (hotelCardContainer.value) {
        observer.value.observe(hotelCardContainer.value);
    }
};
onMounted(() => {
    if (mobileObserving && isMobile.value) {
        startObserving();
    }
});
onBeforeUnmount(() => observer.value?.disconnect());

const searchFormStore = useSearchFormStore();
const { urlParams } = storeToRefs(searchFormStore);

const path = computed(() => {
    if (loading) {
        return '';
    }
    const params = urlParams as Ref<HotelUrlParams>;
    if (isStatic) {
        return hotelForm.generateViewUrl({
            cityName:
                hotelItem?.cityName || params.value.cityName?.toLowerCase(),
            hotelName: hotelItem?.engTitle.toLowerCase(),
        }).fullPath;
    } else {
        return hotelForm.generateViewUrl({
            cityName:
                hotelItem?.cityName || params.value.cityName?.toLowerCase(),
            hotelName: hotelItem?.engTitle.toLowerCase(),
            fromDate: hotelItem?.fromDate || params.value.fromDate,
            toDate: hotelItem?.toDate || params.value.toDate,
            adultCount: `${params.value.adultCount || 1}`,
            childCount: `${params.value.childCount || 0}`,
            roomCount: `${params.value.roomCount || 1}`,
        }).fullPath;
    }
});

const reserveHotel = () => {
    if (!hotelItem) {
        return;
    }
    emit('reserve');
    if (isWebApp.value) {
        router.push(path.value);
    } else {
        window.open(path.value, '_blank');
    }
};
</script>

<template>
    <div
        ref="hotelCardContainer"
        class="mr-hotel-card-container"
        :class="{
            'mobile-view': mobileView || isStatic,
            loading,
        }"
        @click="cardClick"
        @mouseenter="emit('mouseenter', $event)"
        @mouseleave="emit('mouseleave', $event)"
    >
        <div class="photos-container" :class="{ 'no-br': loading }">
            <template v-if="!loading">
                <DelayHydration>
                    <ImageCarousel
                        v-if="!singleImg"
                        :class="{ 'no-photo': hasNoPhotos }"
                        :images="photos"
                        :mobile-slider="!mobileView && !isStatic"
                        button-size="small"
                        :preload-first-image
                        :mobile-image-fit="hasNoPhotos ? 'none' : 'cover'"
                        no-slider
                        no-border-radius
                        show-button-on-hover
                        pagination
                    />
                    <mr-blur-hash-image
                        v-else
                        :hash="photos?.[0].blurhash ?? undefined"
                        :src="photos?.[0].large || undefined"
                        :border-radius="8"
                        :alt="photos?.[0].alt ?? undefined"
                    />
                </DelayHydration>
            </template>
            <img
                v-else
                alt="loading"
                class="loading-img"
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAVgAAACWCAYAAACb+ZAwAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAO2SURBVHgB7drNbV1FGMfhd4YssrQVsYglhG8HoQLoIOkAqCCmAtsduANSAumAVEA6uEaRYjbR9TIbn+GcixwRy06Ec/8rnmd1vtY/zXlnWn3Cer3Ze/Cwno8aP4yqw6pxWAD/Z60u22ivRxsvp9F/Wz3eP7/701usLzaHX9X4dQlrAXCnOaIvrqqf3hbafvPBm782z3ub/hBXgM+b/+5/Wpr559t3RzfffbSCfXOxOZ5qOikA/rNe/eSbx/un1/cfArusXKcxnRUA9zfql28PHm1bug3sMnNdlrjzi70C4P7mTbB58+u7ZSa7ncH2mo7FFWAH5pYuhwSWy7Zdvda0LgB2Znrf9/s8GnhWAOxUfzgd9Tba0wJgp1q17/to40kBsFOjxjx+tbkFkHDYC4AIgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBLYAMgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUIEViAEIEFCBFYgBCBBQgRWIAQgQUI6aPqvADYrVaXvVc7LwB2qo32el7BjlcFwE6NMV629Xqz1x9OmwJgZ6bqq75a7V+2ar8XADvRql6sHu+fb08RXFX7eRnIFgBfZm7pVfXT5XIb2KW001SnBcAXmabpdGnqcv3hHOzq4NHZVJPIAtzT0tDVwddn1/ft5gfrt++Oeq/jGrVXAHzePBbYrlz/Fdd/Ht9ifbE5nCezJ73ajwXAnZZDAss+1vVY4ON3n7CEtrfpWRvt6WjjiVUtQDufw3l+VVev6v2Ds+Uk1l1f/g3N86TcNJIzGAAAAABJRU5ErkJggg=="
            />
        </div>
        <div class="card-info-container">
            <div class="hotel-info" :class="{ static: isStatic }">
                <div class="title-container">
                    <component
                        :is="disableTitleLink ? 'div' : 'a'"
                        class="title"
                        :class="{ 'loading-skeleton': loading }"
                        :href="disableTitleLink ? '' : path"
                        @click.prevent.stop="selectRoom"
                    >
                        <mr-typography
                            type="h2"
                            size="x-large"
                            weight="700"
                            color="var(--cool-gray-900)"
                        >
                            {{ item.title || '' }}
                        </mr-typography>
                    </component>
                </div>
                <div class="address-container">
                    <StarRating
                        v-if="item.percentStar"
                        class="star-rating"
                        :number-of-stars="5"
                        :percent="item.percentStar"
                        :color="loading ? 'var(--cool-gray-200)' : undefined"
                    />
                    <template v-if="item.address">
                        <mr-icon
                            v-if="!loading"
                            :icon="faLocationDot"
                            class="location-icon"
                            color="var(--cool-gray-600)"
                            size="small"
                        />
                        <div
                            class="address"
                            :class="{ 'loading-skeleton': loading }"
                        >
                            {{ item.address }}
                        </div>
                    </template>
                </div>
                <CardLabels v-if="!loading" :labels="item.labels" />
                <div class="hotel-interactions">
                    <HotelPoints
                        v-show="hotelItem?.points10"
                        class="points"
                        :value="hotelItem?.points10 || 0"
                        size="small"
                    />
                    <mr-typography
                        v-show="hotelItem?.commentsLength"
                        size="small"
                        weight="500"
                        color="var(--cool-gray-600)"
                    >
                        &bull; {{ hotelItem?.commentsLength }} نظر
                    </mr-typography>
                </div>
                <slot name="extra-info" />
            </div>
            <mr-divider
                v-if="!loading"
                class="vertical-divider"
                vertical
                size="170px"
                :thickness="1"
            />
            <mr-divider
                v-if="!loading"
                class="horizontal-divider"
                :thickness="1"
            />
            <span
                v-if="showPriceLoading"
                class="pricing-loading loading-skeleton"
            />
            <div v-else class="hotel-pricing">
                <slot name="pricing">
                    <template v-if="payable">
                        <p
                            v-if="!isStatic"
                            class="nights"
                            :class="{ 'loading-skeleton': loading }"
                        >
                            {{
                                `قیمت ${item.nights} شب اتاق ${item.hotelCapacity} تخته`
                            }}
                        </p>
                        <p
                            v-else
                            class="nights"
                            :class="{ 'loading-skeleton': loading }"
                        >
                            قیمت هر شب از
                        </p>
                        <p
                            v-if="mobileView"
                            class="nights nights-small"
                            :class="{
                                'loading-skeleton': loading,
                                static: isStatic,
                            }"
                        >
                            هر شب از
                        </p>
                        <div class="price-view-container">
                            <mr-price-re
                                v-if="!loading && discountedPrice && !isStatic"
                                class="off-price-container"
                                :price="discountedPrice"
                                :percent
                                type="discount"
                            />
                            <mr-price-re
                                v-if="!loading"
                                class="price"
                                :class="{
                                    'loading-skeleton': loading,
                                }"
                                :price="payable"
                            />
                        </div>
                        <mr-button
                            :class="{
                                'loading-skeleton': loading,
                                'reserve-button': isStatic,
                            }"
                            :link="buttonLink"
                            :href="path"
                            full-width
                            @click.prevent.stop="selectRoom"
                        >
                            مشاهده اتاق‌ها و رزرو
                        </mr-button>
                    </template>
                    <template v-else>
                        <div class="floating-btn-container">
                            <mr-button
                                v-if="floatingEnabled"
                                class="floating-button"
                                :class="{ 'loading-skeleton': loading }"
                                :link="buttonLink"
                                :href="path"
                                full-width
                                @click.prevent.stop="selectRoom"
                            >
                                استعلام
                            </mr-button>
                            <mr-typography
                                v-else-if="!loading"
                                class="floating-disabled"
                                align="center"
                                weight="500"
                                color="var(--red-400)"
                            >
                                ناموجود در بازه انتخابی
                            </mr-typography>
                        </div>
                    </template>
                </slot>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.mr-hotel-card-container {
    flex: 1;
    @include shadow();
    display: flex;
    align-items: stretch;
    width: 100%;
    min-height: 170px;
    background-color: white;
    border-radius: 12px;
    overflow: hidden;
    &.loading {
        .hotel-info {
            .address-container {
                margin-top: 12px;
            }
        }
        .hotel-pricing {
            justify-content: space-between;
        }
    }
    .photos-container {
        flex: 0 0 240px;
        display: flex;
        max-width: 240px;
        @include mobile-only {
            padding: 5px;
        }
        .loading-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        .no-photo {
            @include mobile-only {
                background-color: usePalette($p-blue, $p-100);
                border-top-left-radius: 12px;
                border-top-right-radius: 12px;
            }
        }
    }
    .card-info-container {
        flex: 1;
        display: flex;
        align-items: stretch;
        .horizontal-divider {
            display: none;
        }
        .hotel-info {
            display: flex;
            flex-direction: column;
            flex: 1;
            padding: 20px;
            &.static {
                height: 148px;
            }

            .hotel-interactions {
                display: flex;
                align-items: center;
                gap: 4px;
                .points {
                    align-self: flex-start;
                }
            }
            .title-container {
                align-self: stretch;
                display: flex;
                align-items: center;
                .points {
                    margin-right: auto;
                }
                .title {
                    &:hover {
                        text-decoration: none;
                    }
                }
            }
            .address-container {
                display: flex;
                align-items: flex-start;
                margin-top: 4px;
                .star-rating {
                    margin-left: 8px;
                }
                .address {
                    @include tg-s($w5, usePalette($p-cool-gray, $p-500));
                }
            }
        }
        .pricing-loading {
            width: 95%;
            height: 40px;
            margin: 8px;
        }
        .hotel-pricing {
            flex: 0 0 230px;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 12px;
            .nights {
                @include tg-s($w5, usePalette($p-cool-gray, $p-600));
                margin: 0;
                &.nights-small:not(.static) {
                    display: none;
                }
            }

            .price-view-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-bottom: 8px;
                margin-top: auto;
                .off-price-container {
                    display: flex;
                    align-items: center;
                }
            }
        }
    }
}

.floating-btn-container {
    display: flex;
    align-items: flex-end;
    height: 100%;
    width: 100%;

    .floating-disabled {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
    }
}

@mixin mobileStyle {
    cursor: pointer;
    flex-direction: column;
    min-height: unset;
    &.loading {
        min-height: 188px;
        .hotel-pricing {
            margin-top: auto;
        }
    }
    .photos-container {
        flex: 0 0 170px;
        max-width: unset;
        height: 170px;
        width: 100%;
    }
    .card-info-container {
        padding: 0;
        flex-direction: column;
        width: 100%;
        .vertical-divider {
            display: none;
        }
        .horizontal-divider {
            display: flex;
        }
        .hotel-info {
            width: 100%;
            flex: 0 0 auto;
            padding: 12px;
        }
        .hotel-pricing {
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-end;
            justify-content: space-between;
            width: 100%;
            margin-top: auto;
            flex: 0 0 auto;
            padding: 12px;
            .nights {
                padding: 4px 0;
            }
            .price-view-container {
                align-items: flex-end;
                margin-bottom: 0;
            }
            .reserve-button {
                display: none;
            }
        }
    }
}

.mobile-view {
    @include mobileStyle();
}

@media (max-width: $app-mobile-max-width) {
    .mr-hotel-card-container {
        @include mobileStyle();
    }
}
</style>
